import { Directive, EventEmitter, Host, Input, OnDestroy, OnInit, Output, Self } from '@angular/core';
import { Subject } from 'rxjs';
import { VideoViewerComponent } from '../components';
import { takeUntil } from 'rxjs/operators';
import { AssetUtils } from '@telespot/sdk';
import { VideoROIOverlay } from './video-rois-viewer.directive';
import { ViewerMode } from '../types';

@Directive({
    selector: '[tsVideoRoisDrawer]',
    standalone: false
})
export class VideoRoisDrawerDirective implements OnInit, OnDestroy {
  @Input() activeMode: ViewerMode = ViewerMode.drawing;
  @Input() setViewerMode: (mode: ViewerMode) => void;
  @Output() videoROIAdded = new EventEmitter<Partial<VideoROIOverlay>>();

  private _destroy: Subject<void> = new Subject();
  protected _tempROI: Partial<VideoROIOverlay>;

  constructor(@Host() @Self() protected viewerComponent: VideoViewerComponent) {}
  ngOnInit() {
    this.viewerComponent.canvas.nativeElement.addEventListener('mousedown', this._mouseDownHandler.bind(this));
    this.viewerComponent.canvas.nativeElement.addEventListener('mouseup', this._stopPan);
    this.viewerComponent.frameDrawn$.pipe(takeUntil(this._destroy)).subscribe((_) => {
      if (this._tempROI) {
        this.viewerComponent.ctx.strokeStyle = 'white';
        this.viewerComponent.ctx.strokeRect(this._tempROI.x, this._tempROI.y, this._tempROI.w, this._tempROI.h),
          this.viewerComponent.ctx.stroke();
      }
    });
  }

  ngOnDestroy(): void {
    this._destroy.next();
  }

  private _mouseDownHandler = (event) => {
    if (event.shiftKey) {
      if (this.setViewerMode) {
        this.setViewerMode(ViewerMode.drawing);
      }
      // Initialize ROI
      const pos = this._getImagePosition(event);
      this._tempROI = {
        x: pos.x,
        y: pos.y,
        w: 0,
        h: 0,
        t1: this.viewerComponent.video.nativeElement.currentTime,
      };
      this.viewerComponent.canvas.nativeElement.addEventListener('mousemove', this._mouseMoveHandler);
      this.viewerComponent.video.nativeElement.pause();
      console.log(`ROI updated to ${AssetUtils.toString(this._tempROI)}`);
    }
  };
  private _mouseMoveHandler = (event) => {
    if (!this._tempROI?.x || !this._tempROI?.y) return;
    if (this.activeMode === ViewerMode.drawing) {
      const pos = this._getImagePosition(event);

      this._tempROI.w = pos.x - this._tempROI.x;
      this._tempROI.h = pos.y - this._tempROI.y;
    }
  };

  private _stopPan = (event) => {
    if (this._tempROI) {
      console.log(`ROI FINISHED: ${AssetUtils.toString(this._tempROI)}`);
      this.videoROIAdded.emit({
        x: this._tempROI.x,
        y: this._tempROI.y,
        w: this._tempROI?.w,
        h: this._tempROI?.h,
        t1: this._tempROI.t1,
      });
      this._tempROI = undefined;
    }
    if (this.setViewerMode) {
      this.setViewerMode(ViewerMode.idle);
    }

    this.viewerComponent.canvas.nativeElement.removeEventListener('mousemove', this._mouseMoveHandler);
  };

  private _getImagePosition = (event) => {
    const tform = this.viewerComponent.canvasTransform;
    return {
      x: ((event.offsetX - tform[4]) * 1) / tform[0],
      y: ((event.offsetY - tform[5]) * 1) / tform[0],
    };
  };
}
